import { useEffect, useState } from 'react';
import rison from 'rison';
import moment from 'moment/moment';
import { useHistory } from 'react-router-dom';
import { message } from 'antd';
import { t } from '@aibi-ui/core';
import { styled } from '@aibi-ui/core';
import {
  deleteChartFavoriteAPI,
  getChartListAPI,
  getChatIsFavoriteAPI,
  postChartFavoriteAPI,
} from '../../../api/chart';
import ListView from '../components/ListView';
import handleResourceExport from '../../../utils/export';
import DeleteModal from '../../../components/DeleteModal';
import { handleChartDelete } from '../../../views/CRUD/utils';
import { useToasts } from '../../../components/MessageToasts/withToasts';
import Icons from '../../../components/Icons';

const StyledIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(to bottom, #6995ff 0%, #5182fa 100%);
  svg {
    fill: none;
    color: #fff;
  }
`;

const ChartView = ({ setSliceId, setShowChartPreview }) => {
  const [query, setQuery] = useState({
    page: 0,
    page_size: 6,
    filters: [{ col: 'is_saved_by_user', opr: 'eq', value: true }],
  });
  const [activeTab, setActiveTab] = useState(1);
  const tabs = [
    { label: '最近更新', value: 1 },
    { label: '我的收藏', value: 2 },
  ];
  const tabChange = tab => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    if (tab === 1) {
      setQuery({
        ...query,
        filters: [{ col: 'is_saved_by_user', opr: 'eq', value: true }],
      });
    }
    if (tab === 2) {
      setQuery({
        ...query,
        filters: [
          { col: 'is_saved_by_user', opr: 'eq', value: true },
          { col: 'id', opr: 'chart_is_favorite', value: true },
        ],
      });
    }
  };

  const chartIconMap = {
    echarts_timeseries_line: <Icons.LineChartTile viewBox="0 0 16 16" />,
    echarts_timeseries_bar: <Icons.BarChartTile viewBox="0 0 16 16" />,
    echarts_area: <Icons.AreaChartTile viewBox="0 0 16 16" />,
    table: <Icons.TableChartTile viewBox="0 0 16 16" />,
    big_number_total: <Icons.BigNumberChartTile viewBox="0 0 16 16" />,
    pie: <Icons.PieChartTile viewBox="0 0 16 16" />,
  };

  const [type, setType] = useState('list');
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const getList = async () => {
    const q = rison.encode({
      order_column: 'changed_on_delta_humanized',
      order_direction: 'desc',
      ...query,
    });
    const {
      json: { count, result },
    } = await getChartListAPI({ q });
    const list = result.map(r => ({
      ...r,
      title: r.slice_name,
      date: moment(r.changed_on_utc).format('YYYY-MM-DD HH:mm'),
      icon: chartIconMap[r.viz_type] ? (
        <StyledIcon>{chartIconMap[r.viz_type]}</StyledIcon>
      ) : null,
    }));
    const newList = await getFavorite(list);
    setList(newList);
    setTotal(count);
  };
  /**
   * 获取图表是否收藏
   * @returns {Promise<[]>}
   */
  const getFavorite = async list => {
    if (list.length === 0) return [];
    const q = rison.encode(list.map(r => r.id));
    const {
      json: { result },
    } = await getChatIsFavoriteAPI({ q });
    return list.map(r => {
      let favorite = false;
      const target = result.find(x => r.id === x.id);
      if (target) {
        favorite = target.value;
      }
      return { ...r, favorite };
    });
  };

  /**
   * 导出
   * @param chartsToExport
   */
  const onExport = chartsToExport => {
    const ids = chartsToExport.map(({ id }) => id);
    handleResourceExport('chart', ids);
  };
  const [chartToDelete, setChartToDelete] = useState(null);
  const { addSuccessToast, addDangerToast } = useToasts();
  /**
   * 删除
   * @param original
   */
  const handleDelete = original =>
    handleChartDelete(original, addSuccessToast, addDangerToast, getList);

  const actions = ['导出', '删除'];
  /**
   * 导出 || 删除
   * @param item
   * @param key
   */
  const onAction = (item, key) => {
    switch (key) {
      case '导出':
        onExport([item]);
        return;
      case '删除':
        setChartToDelete(item);
        return;
      default:
        break;
    }
  };
  useEffect(() => {
    getList();
  }, [query.page, query.page_size, query.filters]);

  const history = useHistory();
  /**
   * 查看更多
   */
  const onMore = () => {
    history.push('/chart/list/');
  };
  /**
   * 新建图表
   */
  const onAdd = () => {
    history.push('/chart/add');
  };
  /**
   * 查看详情
   * @param chart
   */
  const onDetail = chart => {
    setSliceId(chart.id);
    setShowChartPreview(true);
  };
  useEffect(() => {
    if (type === 'card') {
      setQuery({ ...query, page_size: 6 });
    }
    if (type === 'list') {
      setQuery({ ...query, page_size: 6 });
    }
  }, [type]);
  /**
   * 收藏 || 取消收藏
   * @param chart
   * @param favorite
   * @returns {Promise<void>}
   */
  const onFavorite = async (chart, favorite) => {
    if (favorite) {
      await postChartFavoriteAPI(chart.id);
      message.success('已收藏图表');
    } else {
      await deleteChartFavoriteAPI(chart.id);
      message.success('已取消收藏图表');
    }
    const newList = list.map(r => {
      if (r.id === chart.id) {
        r.favorite = favorite;
      }
      return r;
    });
    setList(newList);
  };
  return (
    <>
      <ListView
        title="图表"
        activeTab={activeTab}
        tabs={tabs}
        tabChange={tabChange}
        moreText="查看所有"
        onMore={onMore}
        addText="新建图表"
        onAdd={onAdd}
        type={type}
        list={list}
        onTypeChange={type => setType(type)}
        onDetail={onDetail}
        current={query.page + 1}
        pageSize={query.page_size}
        total={total}
        onPageChange={page => setQuery({ ...query, page: page - 1 })}
        onFavorite={onFavorite}
        actions={actions}
        onAction={onAction}
      />
      {chartToDelete && (
        <DeleteModal
          description={
            <>
              {t('Are you sure you want to delete')}{' '}
              <b>{chartToDelete.slice_name}</b>?
            </>
          }
          onConfirm={() => {
            handleDelete(chartToDelete);
            setChartToDelete(null);
          }}
          onHide={() => setChartToDelete(null)}
          open={!!chartToDelete}
          title={t('Please confirm')}
        />
      )}
    </>
  );
};

export default ChartView;
